/* Hook-free: the portal renders the same rule list from its own source. */

import { useTranslation } from "react-i18next";
import "../../../../i18n";
import { Notice, ValidationRule } from "../../../../design/components";
import { dateTime } from "../../../../lib/format";
import type { ValidationResult } from "../../../../api/types";
import styles from "../DocumentInspector.module.css";

const SEVERITY_ORDER = { fatal: 0, warning: 1 } as const;
const RULE_STATE = { fatal: "fail", warning: "warn" } as const;

export function sortFailures(failures: ValidationResult["failures"]): ValidationResult["failures"] {
  return [...failures].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

/** The "n of m rules failed" head note; null until a run exists. */
export function ValidationSummary({ result }: { result: ValidationResult | undefined }) {
  const { t } = useTranslation();
  if (!result || result.state === "not_run") return null;
  return (
    <span className={styles.sub}>
      {t("documents.inspector.validation.summary", "{{failed}} of {{total}} rules failed", {
        failed: result.failures.length,
        total: result.rulesEvaluated,
      })}
    </span>
  );
}

/** The body alone, so the portal can feed it from its own source. */
export function ValidationBody({ result, isError }: { result: ValidationResult | undefined; isError: boolean }) {
  const { t } = useTranslation();
  if (isError) return <Notice kind="fail">{t("documents.inspector.validation.loadError", "The validation result could not be loaded.")}</Notice>;
  if (!result) return null;
  if (result.state === "not_run") {
    return <Notice kind="info">{t("documents.inspector.validation.notRun", "Validation has not run for this document yet")}</Notice>;
  }
  return (
    <>
      {result.ranAt ? (
        <p className={styles.sub}>{t("documents.inspector.validation.ranAt", "Ran {{when}}", { when: dateTime(result.ranAt) })}</p>
      ) : null}
      {result.failures.length === 0 ? (
        <Notice kind="ok">{t("documents.inspector.validation.noFailures", "No rule failures.")}</Notice>
      ) : (
        sortFailures(result.failures).map((failure) => (
          <ValidationRule
            key={`${failure.code}-${failure.location ?? ""}`}
            code={failure.code}
            title={failure.title}
            state={RULE_STATE[failure.severity]}
            {...(failure.location ? { location: failure.location } : {})}
          />
        ))
      )}
    </>
  );
}

